import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import Image from '@tiptap/extension-image';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from 'tiptap-markdown';
import Toolbar from './Toolbar';
import './RichEditor.css';

interface RichEditorProps {
  content: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Shows a counter for the markdown length; going over it is the caller's to block. */
  maxLength?: number;
}

const RichEditor: React.FC<RichEditorProps> = ({
  content,
  onChange,
  placeholder = 'Write something...',
  disabled = false,
  autoFocus = false,
  maxLength,
}) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Image,
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder }),
      Markdown.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const md = (editor.storage as any).markdown.getMarkdown();
      onChangeRef.current(md);
    },
  });

  useEffect(() => {
    if (editor && disabled !== undefined) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  // Focus after editor is created and EditorContent has mounted
  const hasFocused = useRef(false);
  useEffect(() => {
    if (autoFocus && editor && !hasFocused.current) {
      hasFocused.current = true;
      // Use DOM-level focus to avoid ProseMirror transaction mismatch
      setTimeout(() => {
        if (!editor.isDestroyed) {
          editor.view.dom.focus();
        }
      }, 50);
    }
  }, [autoFocus, editor]);

  return (
    <div className={`re-container ${disabled ? 're-container--disabled' : ''}`}>
      <Toolbar editor={editor} />
      <EditorContent editor={editor} className="re-content" />
      {maxLength !== undefined && (
        <div className={`re-counter ${content.trim().length > maxLength ? 're-counter--over' : ''}`} aria-live="polite">
          {content.trim().length}/{maxLength}
        </div>
      )}
    </div>
  );
};

export default RichEditor;
